/**
 * Cascade Predictor — "Predicciones Recientes" drawer.
 *
 * Mirrors the structure of the S/R Setups drawer (the one already in
 * LiqMapPage.jsx around lines 1934-2090) so the user experience stays
 * symmetrical between features.
 *
 * Data source: GET /api/liqmap/predictor/recent/list (verifier v2).
 * Outcomes shown: ✓ acierto · ✗ fallo · ➖ no_evaluado · ⌛ pendiente.
 *
 * NOTE: no "Compartir en X" button yet — we only re-introduce sharing
 * after the verified-cases pool grows enough to be meaningful (same
 * pause policy applied to S/R during the Plan A migration window).
 */
import { useEffect, useState } from "react";
import { Loader2, X as XIcon } from "lucide-react";
import { tLm } from "../../i18n/liqmap";

const COINS = [null, "BTC", "ETH", "SOL", "BNB", "XRP", "ZEC"];

export const CascadeRecentDrawer = ({ open, onClose, token, apiBase, initialCoin = null, lang = "es" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [coin, setCoin] = useState(initialCoin);

  // Re-apply initialCoin when the drawer is re-opened (e.g. user clicks
  // "Historial" from a different coin's banner).
  useEffect(() => {
    if (open) setCoin(initialCoin);
  }, [open, initialCoin]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const q = `days=14${coin ? `&coin=${coin}` : ""}`;
        const res = await fetch(`${apiBase}/liqmap/predictor/recent/list?${q}`, { headers });
        const json = await res.json();
        if (cancelled) return;
        setData(res.ok
          ? json
          : { items: [], stats: { total: 0 }, error: json?.detail });
      } catch (e) {
        if (!cancelled) setData({ items: [], stats: { total: 0 }, error: "network" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, coin, token, apiBase]);

  if (!open) return null;

  const items = data?.items || [];
  const stats = data?.stats || {};
  const tasa = stats.tasa_real_pct;
  const tasaCol = tasa == null ? "#888" : tasa >= 65 ? "#00d084" : tasa >= 45 ? "#f0c800" : "#ff3b5c";

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString([], {
        month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };
  const fmtPrice = (v, coinHint) => {
    if (v == null) return "—";
    const digits = (coinHint === "XRP" || (coinHint === "SOL" && v < 100)) ? 4 : 2;
    return `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: digits })}`;
  };

  return (
    <div
      className="absolute inset-0 z-40 flex items-end justify-center bg-black/65 px-2 pb-4 sm:items-center sm:pb-0"
      onClick={onClose}
      data-testid="cascade-recent-overlay"
    >
      <div
        className="relative w-full max-w-2xl overflow-hidden rounded-2xl border shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "rgba(8,5,8,0.97)",
          borderColor: "rgba(249,115,22,0.35)",
          maxHeight: "82vh",
        }}
        data-testid="cascade-recent-drawer"
      >
        <div className="flex items-start justify-between border-b border-white/[0.06] px-4 py-3">
          <div>
            <p className="font-mono font-bold" style={{ color: "#f97316", fontSize: 10, letterSpacing: "2px" }}>
              PREDICCIONES RECIENTES · 14 días
            </p>
            <p className="mt-0.5 font-mono text-[9px] text-white/50">
              Cascade Predictor IA — verificadas con velas reales (touch-then-bounce)
            </p>
            <p
              className="mt-0.5 font-mono text-[9px] text-white/35"
              data-testid="cascade-recent-coverage-disclaimer"
            >
              {tLm(lang).cascade?.coverage_disclaimer}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            data-testid="cascade-recent-close"
            className="inline-flex h-7 w-7 items-center justify-center rounded text-white/60 hover:text-white"
          >
            <XIcon size={14} />
          </button>
        </div>

        <div className="overflow-y-auto" style={{ maxHeight: "calc(82vh - 64px)" }}>
          {/* Coin filter chips */}
          <div className="flex flex-wrap gap-1.5 border-b border-white/5 px-4 py-2.5">
            {COINS.map((c) => {
              const active = coin === c;
              return (
                <button
                  key={c || "all"}
                  type="button"
                  onClick={() => setCoin(c)}
                  data-testid={`cascade-recent-coin-${c || "all"}`}
                  className="rounded-md px-2.5 py-1 font-mono text-[9px] font-bold transition-all"
                  style={active
                    ? { background: "rgba(249,115,22,0.18)", border: "1px solid rgba(249,115,22,0.6)", color: "#f97316" }
                    : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.55)" }
                  }
                >
                  {c || "TODAS"}
                </button>
              );
            })}
          </div>

          {loading && (
            <div className="flex items-center justify-center gap-2 py-10 font-mono text-[11px] text-white/55">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
            </div>
          )}

          {!loading && (
            <>
              {/* Stats summary — 5 columns mirror the S/R panel */}
              <div className="grid grid-cols-5 gap-2 border-b border-white/5 px-4 py-3 text-center font-mono" data-testid="cascade-recent-stats">
                <div>
                  <p className="text-[8px] uppercase tracking-wider text-white/45">Total</p>
                  <p className="text-[14px] font-bold text-white">{stats.total || 0}</p>
                </div>
                <div>
                  <p className="text-[8px] uppercase tracking-wider text-white/45">Aciertos</p>
                  <p className="text-[14px] font-bold" style={{ color: "#00d084" }}>{stats.aciertos || 0}</p>
                </div>
                <div>
                  <p className="text-[8px] uppercase tracking-wider text-white/45">Fallos</p>
                  <p className="text-[14px] font-bold" style={{ color: "#ff3b5c" }}>{stats.fallos || 0}</p>
                </div>
                <div title="Predicciones donde el precio nunca llegó a la zona del cluster (±0.3%) dentro de la ventana de 4h — no cuentan para la tasa.">
                  <p className="text-[8px] uppercase tracking-wider text-white/45">No evaluados</p>
                  <p className="text-[14px] font-bold text-white/55">{stats.no_evaluados || 0}</p>
                </div>
                <div title="Solo sobre predicciones donde el precio sí entró en la zona del cluster (aciertos / (aciertos + fallos)).">
                  <p className="text-[8px] uppercase tracking-wider text-white/45">Tasa real</p>
                  <p className="text-[14px] font-bold" style={{ color: tasaCol }}>
                    {tasa == null ? "—" : `${tasa}%`}
                  </p>
                </div>
              </div>

              {/* PAUSED share — intentionally not present yet. The verifier
                  was just migrated to v2 (Plan A); we will re-introduce the
                  share CTA once the verified pool is large enough to be
                  meaningful (same policy applied to S/R during its own
                  migration window). */}

              {/* Items list — with placeholder rows for coins missing in
                  the period. Stats above come from the backend aggregate
                  and are NEVER affected by these frontend-only rows. */}
              {(() => {
                const ALL = ["BTC", "ETH", "SOL", "BNB", "XRP", "ZEC"];
                const presentCoins = new Set(items.map((x) => x.coin));
                const targetCoins = coin ? [coin] : ALL;
                const missing = targetCoins.filter((c) => !presentCoins.has(c));
                const monitoringLabel = tLm(lang).cascade?.monitoring_predictor || "Monitoreando — sin predicción confirmada aún";
                if (items.length === 0 && missing.length === 0) {
                  return (
                    <p className="px-4 py-10 text-center font-mono text-[11px] text-white/45">
                      Sin predicciones registradas en los últimos {data?.days ?? 14} días{coin ? ` para ${coin}` : ""}.
                    </p>
                  );
                }
                return (
                  <ul className="divide-y divide-white/[0.04]">
                    {items.map((it) => {
                      const out = it.outcome;
                      const chip = out === "acierto"
                        ? { bg: "rgba(0,208,132,0.12)", fg: "#00d084", lbl: "✓ Acierto" }
                        : out === "fallo"
                        ? { bg: "rgba(255,59,92,0.12)", fg: "#ff3b5c", lbl: "✗ Fallo" }
                        : out === "no_evaluado"
                        ? { bg: "rgba(255,255,255,0.05)", fg: "#a0a0a0", lbl: "➖ No evaluado" }
                        : { bg: "rgba(255,255,255,0.06)", fg: "#aaa", lbl: "⌛ Pendiente" };
                      const tipoCol = (it.tipo || "").includes("LONG") ? "#ff3b5c" : "#00d084";
                      return (
                        <li
                          key={it.id}
                          className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 font-mono"
                          data-testid={`cascade-recent-item-${it.id}`}
                        >
                          <span className="text-[11px] font-bold text-white/85">{it.coin}</span>
                          <span className="text-[10px]" style={{ color: tipoCol }}>{it.tipo}</span>
                          <span className="text-[9px] text-white/55">
                            {fmtPrice(it.precio_prediccion, it.coin)} → {fmtPrice(it.cluster_predicho, it.coin)}
                          </span>
                          <span className="text-[9px] text-white/45">score {it.score}</span>
                          <span className="text-[9px] text-white/40">{fmtDate(it.timestamp_prediccion)}</span>
                          <span
                            className="ml-auto rounded-md px-2 py-0.5 text-[9px] font-bold"
                            style={{ background: chip.bg, color: chip.fg }}
                            data-testid={`cascade-recent-outcome-${out || "pendiente"}`}
                          >
                            {chip.lbl}
                          </span>
                        </li>
                      );
                    })}
                    {missing.map((c) => (
                      <li
                        key={`placeholder-${c}`}
                        className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 font-mono opacity-60"
                        data-testid={`cascade-recent-placeholder-${c}`}
                      >
                        <span
                          className="inline-block rounded px-1.5 py-[1px] text-[10px] font-bold text-white/55"
                          style={{ background: "rgba(255,255,255,0.08)" }}
                        >
                          {c}
                        </span>
                        <span className="text-[10px] italic text-white/45">
                          {monitoringLabel}
                        </span>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CascadeRecentDrawer;
